import React, { useState, useEffect } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import { updateMonetization } from '../../services/MonetizationService';
import { getReinsuranceInstitutions, createReinsurance, updateReinsurance } from '../../services/GuaranteesService';

interface GeneralTabProps {
  data: any;
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement';
  onDataUpdate?: () => void;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ data, guaranteeType, onDataUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const [institutions, setInstitutions] = useState<any[]>([]);
  const [reinsuranceInstitutionId, setReinsuranceInstitutionId] = useState<number | null>(null);
  const [reinsurancePercentage, setReinsurancePercentage] = useState<string>('');
  const [creatingReinsurance, setCreatingReinsurance] = useState(false);
  
  // Helper function to convert string/boolean to boolean or null
  const parseBooleanValue = (value: any): boolean | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
    }
    return null;
  };
  
  const [reinsured, setReinsured] = useState<boolean | null>(parseBooleanValue(data.reinsured));
  const [reinsuredEntityPays, setReinsuredEntityPays] = useState<boolean | null>(parseBooleanValue(data.reinsured_entity_pays));
  
  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const response = await getReinsuranceInstitutions();
        if (response.success && 'data' in response && response.data) {
          setInstitutions(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error fetching reinsurance institutions:', error);
      }
    };
    
    fetchInstitutions();
    
    // Initialize reinsurance data if exists
    if (data.reinsurance_institution_name) {
      const institution = institutions.find(inst => inst.name === data.reinsurance_institution_name);
      if (institution) {
        setReinsuranceInstitutionId(institution.id);
      }
    }
    if (data.reinsurance_percentage) {
      setReinsurancePercentage(data.reinsurance_percentage);
    }
  }, []);
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-AR');
  };

  const getOperationType = (): string => {
    if (guaranteeType === 'loan') return 'préstamo';
    if (guaranteeType === 'check') return 'cheque';
    if (guaranteeType === 'promissory_note') return 'pagaré';
    if (guaranteeType === 'commercial_endorsement') return 'commercial-endorsements';
    return 'préstamo';
  };

  const handleFieldUpdate = async (fieldName: string, value: any) => {
    if (updating) return;
    
    setUpdating(true);
    try {
      const updateData = { 
        [fieldName]: value,
        application_product_line_id: data.application_product_line_id
      };
      const response = await updateMonetization(getOperationType(), data.id_global, updateData);
      
      if (response.success) {
        if (onDataUpdate) {
          onDataUpdate();
        }
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
    } finally {
      setUpdating(false);
    }
  };

  const handleReinsuredChange = (value: string) => {
    const boolValue = value === 'Sí' ? true : value === 'No' ? false : null;
    setReinsured(boolValue);
    if (boolValue === false) {
      setReinsuredEntityPays(false);
      handleFieldUpdate('reinsured_entity_pays', false);
    }
    handleFieldUpdate('reinsured', boolValue);
  };

  const handleReinsuredEntityPaysChange = (value: string) => {
    const boolValue = value === 'Sí' ? true : value === 'No' ? false : null;
    setReinsuredEntityPays(boolValue);
    handleFieldUpdate('reinsured_entity_pays', boolValue);
  };

  const handleReinsuranceInstitutionChange = (value: string) => {
    const institution = institutions.find(inst => inst.name === value);
    if (institution) {
      setReinsuranceInstitutionId(institution.id);
      // Try to create reinsurance if percentage is already set
      if (reinsurancePercentage && parseFloat(reinsurancePercentage) > 0) {
        handleCreateReinsurance(institution.id, parseFloat(reinsurancePercentage));
      }
    }
  };

  const handleReinsurancePercentageChange = (value: string) => {
    setReinsurancePercentage(value);
  };

  const handleReinsurancePercentageBlur = () => {
    const percentageNum = parseFloat(reinsurancePercentage);
    const hasExistingData = data.reinsurance_percentage || data.reinsurance_institution_name;
    
    // For updates: call endpoint if percentage is valid (institution already exists)
    // For creates: call endpoint only if both institution and percentage are set
    if (percentageNum > 0 && percentageNum <= 100) {
      if (hasExistingData && reinsuranceInstitutionId) {
        // Update case: call update
        handleCreateReinsurance(reinsuranceInstitutionId, percentageNum);
      } else if (!hasExistingData && reinsuranceInstitutionId) {
        // Create case: only if institution is selected
        handleCreateReinsurance(reinsuranceInstitutionId, percentageNum);
      }
    }
  };

  const handleCreateReinsurance = async (institutionId: number, percentage: number) => {
    if (creatingReinsurance) return;
    
    setCreatingReinsurance(true);
    try {
      // Check if we have existing reinsurance data
      const hasExistingData = data.reinsurance_percentage || data.reinsurance_institution_name;
      
      if (hasExistingData) {
        // Update existing reinsurance
        await updateReinsurance(parseInt(data.id_global), {
          percentage: percentage,
          reinsurance_institution_id: institutionId
        });
      } else {
        // Create new reinsurance
        await createReinsurance({
          id_global: parseInt(data.id_global),
          percentage: percentage,
          reinsurance_institution_id: institutionId
        });
      }
      
      if (onDataUpdate) {
        onDataUpdate();
      }
    } catch (error) {
      console.error('Error saving reinsurance:', error);
    } finally {
      setCreatingReinsurance(false);
    }
  };

  const InfoField = ({ label, value }: { label: string; value: string | number }) => (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'baseline' }}>
      <P type='3' variant='neutral' weight='bold' withMargin={false}>
        {label}:
      </P>
      <P type='3' variant='neutral' withMargin={false}>
        {value || '-'}
      </P>
    </div>
  );

  const renderGeneralData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos Generales
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Numero de Aval" value={data.id_global} />
        <InfoField label="Razon Social" value={data.company_name} />
        <InfoField label="CUIT" value={data.cuit} />
        <InfoField label="Tipo de Operacion" value={data.operation_type} />
        <InfoField label="Tipo de Garantia" value={data.guarantee_type} />
        <InfoField label="Fecha de Carga" value={formatDate(data.created_at)} />
      </div>
    </>
  );

  const renderLoanCreditData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos del credito
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px', alignItems: 'center' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} />
        <InfoField label="Monto neto" value={data.net_monetization_amount} />
        <InfoField label="Divisa" value={data.currency} />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        <InfoField label="Banco acreedor" value={data.creditor} />
        <InfoField label="Sistema de amortización" value={data.amortization_type} />
        <InfoField label="Periodicidad de pagos" value={data.payment_frequency_type} />
        <InfoField label="Destino" value={data.purpose_type_name || data.purposeTypeName || '-'} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
        <InfoField label="Estado de garantia" value={data.status_type} />
        <Card display="flex" gap="4px">
            <DropDown
              id="reinsured"
              title={reinsured === null ? '' : reinsured ? 'Sí' : 'No'}
              items={[
                { label: 'Sí' },
                { label: 'No' }
              ]}
              onChange={handleReinsuredChange}
              size="md"
              disabled={updating}
              label='Reafianzada'
            />
        </Card>
        {reinsured && (
          <>
            <Card display="flex" gap="4px">
                <DropDown
                  id="reinsurance-institution-loan"
                  title={
                    reinsuranceInstitutionId
                      ? institutions.find(inst => inst.id === reinsuranceInstitutionId)?.name || ''
                      : data.reinsurance_institution_name || ''
                  }
                  items={institutions.map(inst => ({ label: inst.name }))}
                  onChange={handleReinsuranceInstitutionChange}
                  size="md"
                  disabled={updating || creatingReinsurance}
                  label='Entidad reafianzadora'
                />
            </Card>
            <Card display="flex" gap="4px">
                <Input
                  id="reinsurance-percentage-loan"
                  type="number"
                  value={reinsurancePercentage || data.reinsurance_percentage || ''}
                  onChange={(e) => handleReinsurancePercentageChange(e.target.value)}
                  onBlur={handleReinsurancePercentageBlur}
                  placeholder="0-100"
                  disabled={updating || creatingReinsurance}
                  label='Porcentaje'
                />
            </Card>
          </>
        )}
        <InfoField label="Con desembolso parcial anterior" value="-" />
      </div>
    </>
  );

  const renderCheckData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos del cheque
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px', alignItems: 'center' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} />
        <InfoField label="Monto neto" value={data.net_monetization_amount} />
        <InfoField label="Divisa" value={data.currency} />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        <InfoField label="Banco acreedor" value={data.creditor} />
        <InfoField label="Entidad bancaria" value={data.bank_entity} />
        <InfoField label="Sucursal" value={data.bank_branch} />
        <InfoField label="Formato de cheque" value={data.check_format_type} />
        <InfoField label="Tipo de CHPD" value={data.chpd} />
        <InfoField label="Numero de cheque" value={data.check_number} />
        <InfoField label="Numero de E-Check" value={data.echeck_number} />
        <InfoField label="Codigo postal" value={data.postal_code} />
        <InfoField label="Plazo de clearing" value={data.clearing} />
        <InfoField label="CMC7" value={data.cmc7} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
        <InfoField label="Estado de garantia" value={data.status_type} />
        <Card display="flex" gap="4px">
          <InfoField label="Reafianzada" value=" " />
          <div style={{ minWidth: '120px', flex: '0 0 auto' }}>
            <DropDown
              id="reinsured"
              title={reinsured === null ? '' : reinsured ? 'Sí' : 'No'}
              items={[
                { label: 'Sí' },
                { label: 'No' }
              ]}
              onChange={handleReinsuredChange}
              size="md"
              disabled={updating}
            />
          </div>
        </Card>
        
        {reinsured && (
          <>
            <Card display="flex" gap="4px">
              <InfoField label="Entidad reafianzadora" value=" " />
              <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
                <DropDown
                  id="reinsurance-institution-check"
                  title={
                    reinsuranceInstitutionId
                      ? institutions.find(inst => inst.id === reinsuranceInstitutionId)?.name || ''
                      : data.reinsurance_institution_name || ''
                  }
                  items={institutions.map(inst => ({ label: inst.name }))}
                  onChange={handleReinsuranceInstitutionChange}
                  size="md"
                  disabled={updating || creatingReinsurance}
                />
              </div>
            </Card>
            <Card display="flex" gap="4px">
              <InfoField label="Porcentaje" value=" " />
                <Input
                  id="reinsurance-percentage-check"
                  type="number"
                  value={reinsurancePercentage || data.reinsurance_percentage || ''}
                  onChange={(e) => handleReinsurancePercentageChange(e.target.value)}
                  onBlur={handleReinsurancePercentageBlur}
                  placeholder="0-100"
                  disabled={updating || creatingReinsurance}
                />
            </Card>
          </>
        )}
        
        <Card>
          {reinsured === false ? (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'baseline' }}>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                Paga entidad reafianzadora:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                No
              </P>
            </div>
          ) : (
            <Card display="flex" gap="4px">
              <InfoField label="Paga entidad reafianzadora" value=" " />
              <div style={{ minWidth: '120px', flex: '0 0 auto' }}>
                <DropDown
                  id="reinsured-entity-pays-check"
                  title={reinsuredEntityPays === null ? '' : reinsuredEntityPays ? 'Sí' : 'No'}
                  items={[
                    { label: 'Sí' },
                    { label: 'No' }
                  ]}
                  onChange={handleReinsuredEntityPaysChange}
                  size="md"
                  marginTop="4px"
                  disabled={updating || !reinsured}
                />
              </div>
            </Card>
          )}
        </Card>
        <InfoField label="Fecha estimada de pago" value={formatDate(data.estimated_payment_date)} />
        <InfoField label="Fecha real de pago" value={formatDate(data.payment_date)} />
      </div>
    </>
  );

  const renderPromissoryNoteData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos del pagare
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} />
        <InfoField label="Monto neto" value={data.net_monetization_amount} />
        <InfoField label="Divisa" value={data.currency} />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        <InfoField label="Banco acreedor" value={data.creditor} />
        <InfoField label="Entidad bancaria" value={data.bank_entity} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
        <InfoField label="Endosado" value={data.endorsed === 'true' || data.endorsed === true ? 'Sí' : 'No'} />
        <InfoField label="ID E-Pagare" value={data.epromissory_note_id} />
        <InfoField label="Fecha estimada de pago" value={formatDate(data.estimated_payment_date)} />
        <InfoField label="Fecha real de pago" value={formatDate(data.payment_date)} />
        <InfoField label="Estado de garantia" value={data.status_type} />
        <Card>
          <DropDown
            id="reinsured-promissory"
            label="Reafianzada"
            title={reinsured === null ? '' : reinsured ? 'Sí' : 'No'}
            items={[
              { label: 'Sí' },
              { label: 'No' }
            ]}
            onChange={handleReinsuredChange}
            size="md"
            marginTop="4px"
            disabled={updating}
          />
        </Card>
        <Card>
          {reinsured === false ? (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'baseline' }}>
              <P type='3' variant='neutral' weight='bold' withMargin={false}>
                Paga entidad reafianzadora:
              </P>
              <P type='3' variant='neutral' withMargin={false}>
                No
              </P>
            </div>
          ) : (
            <DropDown
              id="reinsured-entity-pays-promissory"
              label="Paga entidad reafianzadora"
              title={reinsuredEntityPays === null ? '' : reinsuredEntityPays ? 'Sí' : 'No'}
              items={[
                { label: 'Sí' },
                { label: 'No' }
              ]}
              onChange={handleReinsuredEntityPaysChange}
              size="md"
              marginTop="4px"
              disabled={updating || !reinsured}
            />
          )}
        </Card>
        {reinsured && (
          <>
            <Card>
              <DropDown
                id="reinsurance-institution-promissory"
                label="Entidad reafianzadora"
                title={
                  reinsuranceInstitutionId
                    ? institutions.find(inst => inst.id === reinsuranceInstitutionId)?.name || ''
                    : data.reinsurance_institution_name || ''
                }
                items={institutions.map(inst => ({ label: inst.name }))}
                onChange={handleReinsuranceInstitutionChange}
                size="md"
                marginTop="4px"
                disabled={updating || creatingReinsurance}
              />
            </Card>
            <Card>
              <Input
                id="reinsurance-percentage-promissory"
                label="Porcentaje"
                type="number"
                value={reinsurancePercentage || data.reinsurance_percentage || ''}
                onChange={(e) => handleReinsurancePercentageChange(e.target.value)}
                onBlur={handleReinsurancePercentageBlur}
                placeholder="0-100"
                disabled={updating || creatingReinsurance}
              />
            </Card>
          </>
        )}
      </div>
    </>
  );

  const renderCommercialEndorsementData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos del aval comercial
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px', alignItems: 'center' }}>
        <InfoField label="Monto bruto" value={data.monetization_amount} />
        <InfoField label="Divisa" value={data.currency} />
        <InfoField label="Fecha de emision" value={formatDate(data.issue_date)} />
        <InfoField label="Fecha de vencimiento" value={formatDate(data.due_date)} />
        <InfoField label="Acreedor" value={data.creditor} />
        <InfoField label="Plazo en dias" value={data.term_in_days} />
        <InfoField label="Destino" value={data.purpose_type || '-'} />
        <InfoField label="Estado de garantia" value={data.status_type} />
        <Card display="flex" gap="4px">
          <InfoField label="Reafianzada" value=" " />
          <div style={{ minWidth: '120px', flex: '0 0 auto' }}>
            <DropDown
              id="reinsured-commercial"
              title={reinsured === null ? '' : reinsured ? 'Sí' : 'No'}
              items={[
                { label: 'Sí' },
                { label: 'No' }
              ]}
              onChange={handleReinsuredChange}
              size="md"
              disabled={updating}
            />
          </div>
        </Card>
        {reinsured && (
          <>
            <Card display="flex" gap="4px">
              <InfoField label="Entidad reafianzadora" value=" " />
              <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
                <DropDown
                  id="reinsurance-institution-commercial"
                  title={
                    reinsuranceInstitutionId
                      ? institutions.find(inst => inst.id === reinsuranceInstitutionId)?.name || ''
                      : data.reinsurance_institution_name || ''
                  }
                  items={institutions.map(inst => ({ label: inst.name }))}
                  onChange={handleReinsuranceInstitutionChange}
                  size="md"
                  disabled={updating || creatingReinsurance}
                />
              </div>
            </Card>
            <Card display="flex" gap="4px">
              <InfoField label="Porcentaje" value=" " />
                <Input
                  id="reinsurance-percentage-commercial"
                  type="number"
                  value={reinsurancePercentage || data.reinsurance_percentage || ''}
                  onChange={(e) => handleReinsurancePercentageChange(e.target.value)}
                  onBlur={handleReinsurancePercentageBlur}
                  placeholder="0-100"
                  disabled={updating || creatingReinsurance}
                />
            </Card>
          </>
        )}
      </div>
    </>
  );

  const renderAdditionalData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos adicionales
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px', alignItems: 'center' }}>
        <InfoField label="Referido" value={data.referrer_name} />
        <InfoField label="Socio Protector" value="-" />
        <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de cancelada
            </P>
          <Card margin={{ top: '2' }}>
            <DatePicker
              defaultValue={data.cancelled_date || ''}
              onChange={(date: string) => handleFieldUpdate('cancelled_date', date)}
              disabled={updating}
              displayFormat="DD/MM/AA"
            />
          </Card>
        </Card>
        <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de anulada
            </P>
          <Card margin={{ top: '2' }}>
            <DatePicker
              defaultValue={data.annulled_date || ''}
              onChange={(date: string) => handleFieldUpdate('annulled_date', date)}
              disabled={updating}
              displayFormat="DD/MM/AA"
              alignLeft={true}
            />
          </Card>
        </Card>
        <InfoField label="Fecha envio o firma certificado" value="-" />
      </div>
    </>
  );

  const renderLoanRateData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Tasa y Gracia
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Tasa" value={data.rate_type} />
        <InfoField label="Puntos porcentuales" value={data.percentage_point} />
        <InfoField label="Periodo de gracia" value={data.grace_period} />
      </div>
    </>
  );

  const renderCheckDrawerData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos del librador
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Librador" value={data.drawer_name} />
        <InfoField label="Segmentación" value="-" />
        <InfoField label="Tipo de segmentación" value="-" />
        <InfoField label="CUIT Librador" value={data.drawer_cuit} />
        <InfoField label="Nombre del Librador" value="-" />
      </div>
    </>
  );

  const renderPromissoryNoteDrawerData = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Datos del librador
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Librador" value={data.drawer_name} />
        <InfoField label="Tipo de segmentación" value="-" />
        <InfoField label="CUIT Librador" value={data.drawer_cuit} />
        <InfoField label="Nombre del Librador" value="-" />
      </div>
    </>
  );

  const renderBalances = () => (
    <>
      <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
        Saldos
      </H>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '16px', marginBottom: '32px' }}>
        <InfoField label="Saldo Operativo" value={data.operational_balance || '-'} />
        <InfoField label="Saldo Operativo (Bruto)" value={data.gross_operational_balance || '-'} />
        <InfoField label="Saldo Vigente" value={data.current_balance || '-'} />
        <InfoField label="Saldo Vigente Ponderado" value={data.weighted_current_balance || '-'} />
      </div>
    </>
  );

  return (
    <Card spacing="4">
      {renderGeneralData()}
      
      {guaranteeType === 'loan' && renderLoanCreditData()}
      {guaranteeType === 'check' && renderCheckData()}
      {guaranteeType === 'promissory_note' && renderPromissoryNoteData()}
      {guaranteeType === 'commercial_endorsement' && renderCommercialEndorsementData()}
      
      {renderAdditionalData()}
      
      {guaranteeType === 'loan' && renderLoanRateData()}
      {guaranteeType === 'check' && renderCheckDrawerData()}
      {guaranteeType === 'promissory_note' && renderPromissoryNoteDrawerData()}
      
      {renderBalances()}
    </Card>
  );
};

export default GeneralTab;
